/**
 * @fileoverview SoundCheckQuestion component for rendering a sound check question
 *
 * @example
 * // Usage in component:
 * import { SoundCheckQuestion } from './SoundCheck'
 *
 * <SoundCheckQuestion onSubmit={onSubmit} question={question} />
 */

import { useContext, useState } from 'react'
import { AudioPlayer } from '../AudioPlayer/AudioPlayer'

import { useTranslation } from 'react-i18next'
import { ModalContext } from '../../ModalContext'
import { DesktopProgress, MobileProgress } from '../Progress/Progress'
import { Text } from '../typography/Typography'
import { PromptModalContent } from './PromptModalContent'
import { Title } from './sections/Title'
import css from './SoundCheck.module.css'

/**
 * @function SoundCheckQuestion
 * @description Component for rendering a sound check question
 * @param {Novoic.CheckboxQuestionProps} props - The props for the component
 * @returns {JSX.Element} The SoundCheckQuestion component
 */
export function SoundCheckQuestion({ onSubmit, question }: Novoic.CheckboxQuestionProps) {
    const { id, question: body } = question
    const { title, subtext, audio } = body as Novoic.SoundCheckQuestionBody

    const [isDisabled, setIsDisabled] = useState<boolean>(false)
    const { t } = useTranslation()

    const modal = useContext(ModalContext)

    function onConfirm() {
        setIsDisabled(true)
        return onSubmit(id, {
            checkboxes: {
                'can-hear-sound': true,
            },
        })
    }

    function onClose() {
        setIsDisabled(false)
        return Promise.resolve()
    }

    function onNoSound() {
        setIsDisabled(true)
        return onSubmit(id, {
            checkboxes: {
                'can-hear-sound': false,
            },
        })
    }

    return (
        <div className={css.container}>
            <MobileProgress />
            {title && <Title flexible>{t(title)}</Title>}
            {subtext && <Text>{t(subtext)}</Text>}

            <div className={css.player}>
                <AudioPlayer
                    audio={{
                        file: t(audio.file),
                        duration: parseInt(t(`${audio.duration}`)),
                    }}
                    titles={{ start: t('soundCheck.audioStart'), stop: t('soundCheck.audioStop') }}
                    isDisabled={isDisabled}
                    onEnded={() => {
                        modal.open({
                            render: ({ onConfirm: defaultConfirm, onCancel: defaultCancel }) => (
                                <PromptModalContent
                                    caption={t('modals.soundCheck.caption')}
                                    onConfirm={async () => {
                                        await onConfirm()
                                        defaultConfirm()
                                    }}
                                    onCancel={async () => {
                                        await onNoSound()
                                        defaultCancel()
                                    }}
                                />
                            ),
                            onClose,
                        })
                    }}
                />
            </div>
            <div className={css.progress}>
                <DesktopProgress />
            </div>
        </div>
    )
}
